import React, { useState } from 'react';
import { Button, Input, Radio } from 'antd';
import { AllState } from 'constants/common';
import type { GovEnrollmentByState, MedicaidEnrollmentDto, MedicareManagementDto } from '../types';
import { govCertInvalid, govComplete } from '../validators';
import MedicareManagerFields from './MedicareManagerFields';
import MedicaidEnrollmentFields from '../MedicaidEnrollment/MedicaidEnrollmentFields';
import MedicaidIntro from '../MedicaidEnrollment/MedicaidIntro';
import { entityHasAgreement } from '../MedicaidEnrollment/seed';
import s from '../common.module.less';

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

interface Props {
    // action needed 深链定位：初始选中的州 chip（在 states 内时生效）
    focusState?: string;
    states: string[];
    medicare: GovEnrollmentByState[];
    medicaid: GovEnrollmentByState[];
    onChange: (medicare: GovEnrollmentByState[], medicaid: GovEnrollmentByState[]) => void;
    // Medicare 未注册时的 managing employee（provider 级一份，不按州）；不传则不问这一题
    management?: MedicareManagementDto | null;
    onManagementChange?: (management: MedicareManagementDto) => void;
    // Medicaid 未注册时的注册问卷（PAVE，provider 级一份）；不传则保留旧的红字提示
    medicaidEnrollment?: MedicaidEnrollmentDto | null;
    onMedicaidEnrollmentChange?: (value: MedicaidEnrollmentDto) => void;
    // individual：solo / 只有一个 provider 的 practice；rendering：2 个以上 provider 的 practice
    medicaidVariant?: 'individual' | 'rendering';
    // Verification 的实体类型（operating agreement 只在有实体时问）
    entityType?: string;
    // 预填依据：Disclosures 的执照处分题答了 No / 已有 payout 账户（「已注册过」由本步的答案实时算）
    licenseActionNo?: boolean;
    payoutOnFile?: boolean;
    // Settings 里问卷有单独的 Medicaid enrollment 板块：这里只留说明，不重复渲染问题
    medicaidInline?: boolean;
    onContinue: () => void;
    continueLabel?: string;
    // group 宿主（ProviderPayerModal）传 true：零侧边对齐标题、footer 分隔线通边
    flush?: boolean;
    // review 行内编辑：只渲染表单 body，动作按钮由宿主（ReviewSection 吸底条）提供
    hideFooter?: boolean;
    onBack?: () => void;
    saving?: boolean;
}

const InfoIcon = () => (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="#60A5FA" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        />
    </svg>
);

const findEntry = (list: GovEnrollmentByState[], state: string) => list.find((g) => g.state === state);

const upsert = (list: GovEnrollmentByState[], state: string, patch: Partial<GovEnrollmentByState>) => {
    const idx = list.findIndex((g) => g.state === state);
    if (idx === -1) return [...list, { state, ...patch }];
    const next = [...list];
    next[idx] = { ...next[idx], ...patch };
    return next;
};

// Government program details：每州两个 Yes/No 问题（Medicare / Medicaid enrolled?）。
// 答 No 不阻塞；证书号一律选填（enrolled=Yes 也不强制），填了才校验格式。
// Medicare 答 No（09/24）：Klarity 代为申请 855I，蓝色说明 + 问 managing employee（10/15，855I 第 6 节）。
const GovProgramStep = ({
    states,
    medicare,
    medicaid,
    onChange,
    onContinue,
    onBack,
    saving,
    continueLabel = 'Continue',
    flush,
    hideFooter,
    focusState,
    management,
    onManagementChange,
    medicaidEnrollment,
    onMedicaidEnrollmentChange,
    medicaidVariant = 'individual',
    entityType,
    licenseActionNo,
    payoutOnFile,
    medicaidInline = true,
}: Props) => {
    const everEnrolled = medicaid.some((g) => g.enrolled === true);
    const [activeState, setActiveState] = useState(focusState && states.includes(focusState) ? focusState : states[0]);
    const effectiveState = states.includes(activeState) ? activeState : states[0];

    const askManagement = !!onManagementChange;
    const complete = govComplete(states, medicare, medicaid, askManagement ? management ?? null : false);

    const renderProgramCard = (program: 'medicare' | 'medicaid', list: GovEnrollmentByState[], state: string) => {
        const title = program === 'medicare' ? 'Medicare' : 'Medicaid';
        const certLabel =
            program === 'medicare' ? 'Medicare certification number / PTAN' : 'Medicaid certification number';
        const notEnrolledNote = `You need to enroll in ${getStateName(state)} Medicaid before we can submit a Medicaid panel.`;
        const entry = findEntry(list, state);
        const showCertError = govCertInvalid(list, state);
        let enrolledValue: 'Yes' | 'No' | undefined;
        if (entry?.enrolled !== undefined) enrolledValue = entry.enrolled ? 'Yes' : 'No';

        const patch = (p: Partial<GovEnrollmentByState>) => {
            const next = upsert(list, state, p);
            onChange(program === 'medicare' ? next : medicare, program === 'medicaid' ? next : medicaid);
        };

        return (
            <div className={s.govCard} key={`${program}-${state}`}>
                <div className={s.govCardTitle}>{title}</div>
                <span className={s.fieldLabel}>Are you already enrolled?</span>
                <Radio.Group value={enrolledValue} onChange={(e) => patch({ enrolled: e.target.value === 'Yes' })}>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                </Radio.Group>
                {entry?.enrolled === true && (
                    <div className={s.certWrap}>
                        <span className={s.subFieldLabel}>{certLabel}</span>
                        <Input
                            value={entry?.certificationNumber || ''}
                            placeholder="Do not enter spaces (ex. 123456789)"
                            onChange={(e) => patch({ certificationNumber: e.target.value })}
                        />
                        {showCertError && (
                            <div className={s.fieldError}>
                                {program === 'medicare' ? 'PTAN' : 'Certification number'} should be 5-10
                                letters/numbers, no spaces or symbols.
                            </div>
                        )}
                    </div>
                )}
                {entry?.enrolled === false && program === 'medicaid' && !onMedicaidEnrollmentChange && medicaidInline && (
                    <div className={s.govNote}>{notEnrolledNote}</div>
                )}
                {/* 09/21：Klarity 自己去各州注册 Medicaid，No 不再拦 Continue，而是打开注册问卷 */}
                {entry?.enrolled === false && program === 'medicaid' && !medicaidInline && (
                    <div className={s.medicareFiling}>
                        <MedicaidIntro
                            title="We will enroll you in Medicaid"
                            text="The enrollment answers are under Medicaid enrollment."
                        />
                    </div>
                )}
                {entry?.enrolled === false && program === 'medicaid' && medicaidInline && onMedicaidEnrollmentChange && (
                    <div className={s.medicareFiling}>
                        <MedicaidIntro
                            title="We will enroll you in Medicaid"
                            text={
                                medicaidVariant === 'rendering'
                                    ? 'You join as a rendering provider under your practice, which answers the business questions. These few are about you, and apply to every state you enroll in.'
                                    : 'The enrollment application needs a few details about your practice. Answer them once and we use them for every state you enroll in.'
                            }
                        />
                        <MedicaidEnrollmentFields
                            variant={medicaidVariant}
                            isCA={state === 'CA'}
                            showOrgAgreement={entityHasAgreement(entityType)}
                            derive={{ licenseActionNo, everEnrolled, payoutOnFile }}
                            value={medicaidEnrollment}
                            onChange={onMedicaidEnrollmentChange}
                        />
                    </div>
                )}
                {entry?.enrolled === false && program === 'medicare' && (
                    <div className={s.medicareFiling}>
                        <div className={s.infoBox}>
                            <InfoIcon />
                            <span>
                                We file your Medicare enrollment in {getStateName(state)}. Your Medicare number (PTAN)
                                arrives by email once it is approved.
                            </span>
                        </div>
                        {askManagement && (
                            <MedicareManagerFields value={management} onChange={onManagementChange!} />
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className={`${s.modalBody} ${flush ? s.flushBody : ''}`}>
                <div className={s.stateChips}>
                    {states.map((st) => (
                        <button
                            key={st}
                            type="button"
                            className={`${s.stateChip} ${st === effectiveState ? s.stateChipActive : ''}`}
                            onClick={() => setActiveState(st)}
                        >
                            {getStateName(st)}
                        </button>
                    ))}
                </div>
                {renderProgramCard('medicare', medicare, effectiveState)}
                {renderProgramCard('medicaid', medicaid, effectiveState)}
            </div>
            {!hideFooter && (
            <div className={`${s.modalFooter} ${flush ? s.flushFooter : ''}`}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    disabled={!complete}
                    loading={saving}
                    onClick={onContinue}
                >
                    {continueLabel}
                </Button>
                {onBack && (
                    <Button block size="large" className={s.backBtn} onClick={onBack}>
                        Back
                    </Button>
                )}
            </div>
            )}
        </>
    );
};

export default GovProgramStep;
